/**
 * Smokey Hill Designs: native free-shipping progress bar.
 * Renders every <shd-free-shipping> instance (header bar, cart drawer, cart page) from one shared state.
 *
 * State source of truth is Liquid (snippets/shd-free-shipping-bar.liquid), which knows product tags,
 * excluded products and discount allocations. After any cart change we re-fetch it with the Section
 * Rendering API (?sections=shd-free-shipping-bar). Triggers:
 *   - Horizon's `cart:update` / `discount:update` events (@theme/events)
 *   - any fetch()/XHR to /cart/add, /cart/change, /cart/update, /cart/clear (apps, quick add, custom code)
 *   - Horizon cart morphs that refresh an instance's data-state attribute
 *   - returning to the tab / bfcache restore
 * Threshold is stored in the shop currency and converted with Shopify.currency.rate.
 */
import { ThemeEvents } from '@theme/events';

const SECTION_ID = 'shd-free-shipping-bar';
const CART_PATH = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Set<SHDFreeShipping>} */
const instances = new Set();
/** @type {{e:number,thr:number,c:number,x:number,xp:string[],cur:string,sc:string} | null} */
let state = null;
let refreshTimer = 0;
/** @type {AbortController | null} */
let inflight = null;

function parseState(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed?.e === 'number' && typeof parsed?.thr === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

function rate() {
  const r = parseFloat(window.Shopify?.currency?.rate ?? '1');
  return Number.isFinite(r) && r > 0 ? r : 1;
}

function activeCurrency() {
  return window.Shopify?.currency?.active || state?.cur || 'USD';
}

/** Threshold in presentment-currency cents (rounded up to a whole unit when converted). */
function thresholdCents() {
  if (!state) return 0;
  const sameCurrency = !state.sc || activeCurrency() === state.sc;
  if (sameCurrency) return state.thr;
  return Math.ceil((state.thr * rate()) / 100) * 100;
}

function formatMoney(cents) {
  const value = cents / 100;
  const whole = Math.round(cents) % 100 === 0;
  const digits = whole ? 0 : 2;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: activeCurrency(),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `$${value.toFixed(digits)}`;
  }
}

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function adopt(next) {
  if (!next) return;
  state = next;
  for (const el of instances) el.render();
}

class SHDFreeShipping extends HTMLElement {
  static observedAttributes = ['data-state'];

  /** @type {string | undefined} */
  #lastStatus;
  #lastMsg = '';
  #celebrateTimer = 0;

  connectedCallback() {
    instances.add(this);
    const own = parseState(this.dataset.state);
    if (own) {
      adopt(own);
    } else {
      this.render();
    }
  }

  disconnectedCallback() {
    instances.delete(this);
    clearTimeout(this.#celebrateTimer);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Horizon's cart morph refreshes data-state with fresh server values (children are skipped).
    if (name === 'data-state' && oldValue !== null && oldValue !== newValue && this.isConnected) {
      adopt(parseState(newValue));
    }
  }

  render() {
    if (!state) return;
    const msgEl = this.querySelector('[data-fsb-msg]');
    const track = this.querySelector('[data-fsb-track]');
    const note = this.querySelector('[data-fsb-note]');

    const goal = thresholdCents();
    const eligible = Math.max(0, state.e);
    const remaining = Math.max(0, goal - eligible);
    let status = 'progress';
    if (eligible <= 0) status = 'empty';
    else if (remaining <= 0) status = 'achieved';
    const progress = status === 'achieved' ? 1 : goal > 0 ? Math.min(1, eligible / goal) : 0;

    const amount = `<span class="shd-fsb__amount">${escapeHtml(formatMoney(remaining))}</span>`;
    const threshold = `<span class="shd-fsb__amount">${escapeHtml(formatMoney(goal))}</span>`;
    const template = this.dataset[`msg${status.charAt(0).toUpperCase()}${status.slice(1)}`] || '';
    const html = escapeHtml(template).replaceAll('[amount]', amount).replaceAll('[threshold]', threshold);

    // Only touch the live region when the text really changes (one announcement per change).
    if (msgEl && html !== this.#lastMsg) {
      msgEl.innerHTML = html;
      this.#lastMsg = html;
    }

    this.style.setProperty('--shd-fsb-progress', String(progress));
    this.dataset.status = status;

    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      track.setAttribute(
        'aria-valuetext',
        status === 'achieved'
          ? 'Free shipping unlocked'
          : `${formatMoney(eligible)} of ${formatMoney(goal)} toward free shipping`
      );
    }

    if (note) note.hidden = !(state.x > 0);

    if (this.#lastStatus && this.#lastStatus !== 'achieved' && status === 'achieved') this.#celebrate();
    this.#lastStatus = status;
  }

  #celebrate() {
    if (reduceMotion.matches) return;
    this.classList.remove('is-celebrating');
    // restart the animation if it was already running
    void this.offsetWidth;
    this.classList.add('is-celebrating');
    clearTimeout(this.#celebrateTimer);
    this.#celebrateTimer = window.setTimeout(() => this.classList.remove('is-celebrating'), 1600);
  }
}

if (!customElements.get('shd-free-shipping')) {
  customElements.define('shd-free-shipping', SHDFreeShipping);
}

/** Fallback when the section fetch fails: recompute from /cart.js using the last known excluded product ids. */
async function refreshFromCartJs(signal) {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`, { signal, headers: { Accept: 'application/json' } });
  const cart = await response.json();
  const excluded = new Set((state?.xp || []).map(String));
  let eligible = 0;
  let excludedQty = 0;
  for (const item of cart.items || []) {
    if (excluded.has(String(item.product_id)) || item.requires_shipping === false) {
      excludedQty += item.quantity;
    } else {
      eligible += item.final_line_price;
    }
  }
  const cartLevel = (cart.cart_level_discount_applications || []).reduce((sum, d) => sum + (d.total_allocated_amount || 0), 0);
  if (cartLevel && cart.items_subtotal_price) eligible -= cartLevel * (eligible / cart.items_subtotal_price);
  if (state) adopt({ ...state, e: Math.max(0, Math.round(eligible)), c: cart.item_count, x: excludedQty });
}

async function refresh() {
  if (!instances.size) return;
  inflight?.abort();
  const controller = new AbortController();
  inflight = controller;
  try {
    const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    url.searchParams.set('sections', SECTION_ID);
    const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    const html = json?.[SECTION_ID];
    if (typeof html !== 'string') throw new Error('Missing section');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = parseState(doc.querySelector('shd-free-shipping')?.getAttribute('data-state'));
    if (next) adopt(next);
  } catch (error) {
    if (controller.signal.aborted) return;
    try {
      await refreshFromCartJs(controller.signal);
    } catch {
      /* keep the last good state */
    }
  } finally {
    if (inflight === controller) inflight = null;
  }
}

function scheduleRefresh(delay = 150) {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(refresh, delay);
}

function isCartMutation(input) {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url;
    if (!raw) return false;
    return CART_PATH.test(new URL(raw, window.location.href).pathname);
  } catch {
    return false;
  }
}

if (!window.__shdFreeShippingHooked) {
  window.__shdFreeShippingHooked = true;

  document.addEventListener(ThemeEvents.cartUpdate, () => scheduleRefresh(120));
  document.addEventListener(ThemeEvents.discountUpdate, () => scheduleRefresh(120));

  // Catch cart changes made by apps or custom code that do not dispatch theme events.
  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const result = nativeFetch.apply(this, args);
    if (isCartMutation(args[0])) {
      result.then(() => scheduleRefresh(), () => {});
    }
    return result;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isCartMutation(url)) this.addEventListener('loadend', () => scheduleRefresh(), { once: true });
    return nativeOpen.call(this, method, url, ...rest);
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRefresh(0);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleRefresh(300);
  });

  // Non-USD presentment: Liquid can't convert the threshold, so make sure JS text is in place promptly.
  window.SHDFreeShipping = { refresh: () => scheduleRefresh(0), get state() { return state; } };
}
